import React, { useEffect, useMemo, useState } from 'react';
import { Bubble, Col, Dropdown, List, Row } from 'tea-component';
import './index.scss';

import SvgIcon from '@/pages/home/components/SvgIcon';
import { useIntl } from 'umi';
import useGameCompetitor from '../../hooks/useGameCompetitor';
import RenderGameIcon from '@/pages/opinion/components/RenderImg/renderGameIcon';
import useGameCountryPublisher from '../../hooks/useGameCountryPublisher';
import RegionCountryItem from '@/pages/opinion/components/Snippets/RegionCountryItem';
import CardCommon from '@/pages/opinion/components/CardCommon';
import AddCompare from '../ComparisonCard/Components/AddCompare';
import { findGameInfo, useGameOperations } from '@/pages/opinion/utils/game';

const SimilarGames = ({ gameInfo, filter, setXAxisList }: any) => {
  const {
    mainGame,
    competitorGame,
    originalGameList,
    changeCompetitorGame,
    deleteCompetitorGame,
    loading,
  } = useGameCompetitor({ filter, gameInfo });
  const { formatMessage } = useIntl();

  const [visible, setVisible] = useState(false);

  const { gameCountryMap } = useGameCountryPublisher({
    selected: originalGameList,
    mainGame,
  });

  useEffect(() => {
    setXAxisList(competitorGame);
  }, [competitorGame]);

  const addGame = () => setVisible(true);

  return (
    <>
      <CardCommon
        title={formatMessage({ id: 'Similar Games' })}
        className="trends-similar"
        loading={loading}
        loadingHeight={150}
      >
        <Row>
          <>
            {originalGameList.map((e, i) => {
              return (
                <Col span={6} key={e.edition_unified_id}>
                  <CardItem
                    data={e}
                    deleteSimilarGame={() => deleteCompetitorGame(i)}
                    changeCompetitorGame={(changeId: string) => {
                      changeCompetitorGame(
                        {
                          ...e,
                          selected_edition_unified_id: changeId,
                        },
                        i,
                      );
                    }}
                    formatMessage={formatMessage}
                    gameCountryMap={gameCountryMap}
                  />
                </Col>
              );
            })}
          </>
          <Col span={6}>
            <div onClick={addGame} className="dbo-trends-add">
              <div className="dbo-trends-add-flex">
                <SvgIcon type="add-border" className="dbo-trends-add-icon" />
                <span className="dbo-trends-add-text">
                  {formatMessage({ id: 'Add & Sort Games' })}
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </CardCommon>
      {visible && (
        <AddCompare
          visible={visible}
          mainGame={mainGame}
          selected={originalGameList}
          close={() => setVisible(false)}
          confirm={(targetList: any[]) => {
            changeCompetitorGame(targetList);
          }}
        />
      )}
    </>
  );
};

export default SimilarGames;

const CardItem = (props: any) => {
  const {
    data,
    gameCountryMap,
    formatMessage,
    deleteSimilarGame,
    changeCompetitorGame,
  } = props;

  const mobileEditionList = useMemo(() => {
    return data?.mobile_edition_list || [];
  }, [data]);

  const selectData = useMemo(() => {
    const [game] = findGameInfo([data], data.selected_edition_unified_id) || [];
    return game || {};
  }, [data]);

  const { country, publisher } = useMemo(() => {
    let key = data?.selected_edition_unified_id;
    return { ...data, ...(gameCountryMap?.[key] || {}) };
  }, [data, gameCountryMap]);

  const { handleChangeGame } = useGameOperations();

  return (
    <Dropdown
      matchButtonWidth
      trigger="click"
      className="dbo-card-dropdown"
      button={
        <div className="dbo-card-box">
          <div className="dbo-card-item">
            <div className="dbo-card-left">
              <RenderGameIcon
                className="dbo-card-img"
                gameInfo={selectData}
              ></RenderGameIcon>
              <div className="dbo-card-info">
                <div className="dbo-card-title">
                  <span
                    className="dbo-card-title-text"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChangeGame(selectData, true);
                    }}
                  >
                    {selectData.entity_name}
                  </span>
                  {mobileEditionList.length !== 0 && (
                    <SvgIcon type="arrow-down" className="exit-icon" />
                  )}
                </div>
                <div className="dbo-card-text">
                  <RegionCountryItem
                    iconClassName="dbo-card-text-img"
                    market={country}
                    showText={false}
                  ></RegionCountryItem>
                  <span className="dbo-card-text-desc">{publisher}</span>
                </div>
              </div>
            </div>
            <Bubble
              dark
              closeOnScroll
              placement="top"
              content={formatMessage({ id: 'Remove this item' })}
            >
              <div
                className="dbo-card-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteSimilarGame();
                }}
              >
                <SvgIcon
                  type="delete-bg-icon"
                  className="dbo-card-delete-icon"
                />
              </div>
            </Bubble>
          </div>
        </div>
      }
      appearance="pure"
      overlayClassName="dbo-card-tip"
    >
      {() => (
        <List type="option">
          <List.Item
            onClick={() => {
              changeCompetitorGame(data.edition_unified_id);
            }}
          >
            <div className="down-flex">
              <RenderGameIcon
                gameInfo={data}
                className="down-img"
              ></RenderGameIcon>
              <span className="down-text">
                {formatMessage({ id: 'All Editions' })}
              </span>
            </div>
          </List.Item>
          {mobileEditionList.map((item: any) => (
            <List.Item
              onClick={() => {
                changeCompetitorGame(item.edition_unified_id);
              }}
            >
              <div className="down-flex">
                <RenderGameIcon
                  gameInfo={item}
                  className="down-img"
                ></RenderGameIcon>
                <span className="down-text">{item.entity_name}</span>
              </div>
            </List.Item>
          ))}
        </List>
      )}
    </Dropdown>
  );
};
